// Drag-and-drop PDF uploader for RAG guideline documents. Base64-encodes the file
// client-side and sends it via UploadDocumentSpecification, which the backend stores to
// disk and forwards to the analysis service to be chunked/embedded (see
// UploadDocumentController.ts's write-then-rollback handling on the backend).
import { useRef, useState } from "react"
import ReactDOM from "react-dom"
import { UploadDocumentSpecification } from "pokal_api"

type Props = {
    onSuccess: () => void
    onClose: () => void
}

export default function UploadDocumentModal({ onSuccess, onClose }: Props) {
    const [title, setTitle] = useState("")
    const [file, setFile] = useState<File | null>(null)
    const [isDragging, setIsDragging] = useState(false)
    const [isUploading, setIsUploading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const inputRef = useRef<HTMLInputElement>(null)

    function handleFile(f: File) {
        if (!f.name.toLowerCase().endsWith(".pdf")) {
            setError("Nur PDF-Dateien sind erlaubt.")
            return
        }
        setError(null)
        setFile(f)
    }

    async function handleUpload() {
        if (!file) return
        setIsUploading(true)
        setError(null)
        try {
            const reader = new FileReader()
            reader.onload = async e => {
                try {
                    const base64 = (e.target?.result as string).split(",")[1]
                    await UploadDocumentSpecification.request({
                        title: title.trim() || file.name,
                        filename: file.name,
                        file_content: base64,
                    })
                    setIsUploading(false)
                    onSuccess()
                    onClose()
                } catch {
                    setIsUploading(false)
                    setError("Upload fehlgeschlagen. Bitte erneut versuchen.")
                }
            }
            reader.readAsDataURL(file)
        } catch {
            setIsUploading(false)
            setError("Upload fehlgeschlagen. Bitte erneut versuchen.")
        }
    }

    const modal = (
        <div
            className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
            onClick={e => { if (e.target === e.currentTarget) onClose() }}
        >
            <div className="bg-white rounded-xl p-6 w-80 shadow-2xl">
                <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-bold text-gray-800">Dokument hochladen</span>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-base">✕</button>
                </div>

                <div className="mb-3">
                    <label className="text-xs text-gray-500 block mb-1">Titel (optional)</label>
                    <input
                        type="text"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        placeholder="z.B. S3-Leitlinie Depression 2024"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                </div>

                <div
                    onDragOver={e => { e.preventDefault(); setIsDragging(true) }}
                    onDragLeave={() => setIsDragging(false)}
                    onDrop={e => {
                        e.preventDefault()
                        setIsDragging(false)
                        if (e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0])
                    }}
                    onClick={() => inputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer mb-4 transition-colors ${
                        isDragging ? "border-blue-400 bg-blue-50" : "border-blue-200 bg-blue-50 hover:border-blue-400"
                    }`}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]) }}
                    />
                    {file ? (
                        <span className="text-xs text-blue-700 font-medium">📎 {file.name}</span>
                    ) : (
                        <>
                            <div className="text-xs text-blue-500 font-semibold">PDF auswählen oder hierher ziehen</div>
                            <div className="text-xs text-blue-300 mt-1">Nur PDF-Dateien · max. 50 MB</div>
                        </>
                    )}
                </div>

                {error && <p className="text-xs text-red-500 mb-3">{error}</p>}

                <button
                    onClick={handleUpload}
                    disabled={!file || isUploading}
                    className="w-full bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-md py-2 text-sm transition-colors"
                >
                    {isUploading ? "Wird hochgeladen…" : "Hochladen"}
                </button>
            </div>
        </div>
    )

    return ReactDOM.createPortal(modal, document.body)
}
